import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Download, History, Loader2, Maximize2, Minimize2, Minus, Paperclip, Send, SquarePen, X } from 'lucide-react';
import { AudioRecorderButton, type AudioGravado } from '@/features/chat/AudioRecorderButton';
import { getErrorMessage } from '@/shared/lib/api/errors';
import { Button, Tooltip } from '@/shared/ui';
import { useAuth } from '@/features/auth/AuthProvider';
import { useConsentimentoIa } from '@/features/auth/consentimentoIaContexto';
import { useAssistenteUi } from './assistenteUi';
import { assistenteService } from './assistenteService';
import { alturaRodapeAcao } from './rodapeAcao';
import { AnexosMia } from './AnexosMia';
import { BotaoMia } from './BotaoMia';
import { AberturaMia } from './AberturaMia';
import { ListaComandosMia } from './ComandosMia';
import { HistoricoMia } from './HistoricoMia';
import { AvatarMia, ItemFioMia } from './ItemFioMia';
import { TextoAssistente } from './TextoAssistente';
import { MAX_ANEXOS_MIA, useAnexosMia } from './useAnexosMia';
import { useComandosMia } from './useComandosMia';
import { useDocaMia } from './useDocaMia';
import { exportarConversa, useConversaMia } from './useConversaMia';
import styles from './AssistenteWidget.module.css';

function baixar(nome: string, conteudo: string) {
  const url = URL.createObjectURL(new Blob([conteudo], { type: 'text/markdown;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = nome;
  link.click();
  URL.revokeObjectURL(url);
}

export function AssistenteWidget() {
  const { aberto, presente, recolhida, abrir, minimizar, fechar } = useAssistenteUi();
  const { rodape: alturaRodape, topo } = useMedidasShell(aberto || presente);
  const conversa = useConversaMia(aberto);
  const consentimento = useConsentimentoIa();
  const fecharRef = useRef(fechar);
  fecharRef.current = fechar;

  // Sem a permissao de IA a Mia nao funciona: pergunta ao abrir e, com
  // "Agora nao", fecha o painel (consentimento-ia.md, R5).
  useEffect(() => {
    if (!aberto) return;
    let ativo = true;
    void consentimento.garantir().then((ok) => {
      if (ativo && !ok) fecharRef.current();
    });
    return () => {
      ativo = false;
    };
  }, [aberto, consentimento]);
  const { user } = useAuth();
  const escolaAnterior = useRef(user?.idEscolaAtiva ?? null);
  const { novaConversa } = conversa;
  // Cada conversa e de uma escola: trocar a escola (pela Mia ou pelo cabecalho) comeca outra.
  useEffect(() => {
    const atual = user?.idEscolaAtiva ?? null;
    if (escolaAnterior.current === atual) return;
    escolaAnterior.current = atual;
    void novaConversa();
  }, [user?.idEscolaAtiva, novaConversa]);
  const { itens, enviando, erro, setErro, historicoPronto } = conversa;
  const [texto, setTexto] = useState('');
  const [transcrevendo, setTranscrevendo] = useState(false);
  const [gravando, setGravando] = useState(false);
  const [maximizado, setMaximizado] = useState(false);
  const [vendoHistorico, setVendoHistorico] = useState(false);
  const doca = useDocaMia(aberto, maximizado);
  const fioRef = useRef<HTMLDivElement>(null);
  const campoRef = useRef<HTMLInputElement>(null);
  const arquivoRef = useRef<HTMLInputElement>(null);
  const anexos = useAnexosMia(setErro);
  const comandos = useComandosMia(texto, user?.acoes ?? [], (comando) => {
    if (comando.enviar) {
      setTexto('');
      void enviar(comando.texto);
    } else {
      setTexto(comando.texto);
      campoRef.current?.focus();
    }
  });

  // Ctrl+K (Cmd+K no Mac) abre a Mia de qualquer tela; com o foco ja nela, minimiza. Na captura,
  // para nenhum handler da tela engolir o atalho antes; editor de texto rico fica com o dele.
  useEffect(() => {
    function atalho(event: KeyboardEvent) {
      if (!(event.ctrlKey || event.metaKey) || event.shiftKey || event.altKey) return;
      if (event.key.toLowerCase() !== 'k' && event.code !== 'KeyK') return;
      const alvo = event.target instanceof HTMLElement ? event.target : null;
      if (alvo?.isContentEditable) return;
      event.preventDefault();
      if (!aberto) abrir();
      else if (alvo?.closest('[data-painel-mia]')) minimizar();
      else campoRef.current?.focus();
    }
    window.addEventListener('keydown', atalho, true);
    return () => window.removeEventListener('keydown', atalho, true);
  }, [aberto, abrir, minimizar]);
  const pendente = [...itens].reverse().find((item) => item.proposta && item.propostaEstado === 'pendente');
  const ultimaDaMia = [...itens].reverse().find((item) => item.papel === 'assistente');

  useEffect(() => {
    if (!aberto || vendoHistorico) return;
    campoRef.current?.focus();
  }, [aberto, vendoHistorico]);

  useEffect(() => {
    const fio = fioRef.current;
    if (!fio) return;
    // Conversa vazia: a saudacao (R26) fica no topo; rolar ao fim a esconderia.
    if (itens.length === 0 && !enviando) {
      fio.scrollTop = 0;
      return;
    }
    if (typeof fio.scrollTo === 'function') {
      fio.scrollTo({ top: fio.scrollHeight, behavior: 'smooth' });
    } else {
      fio.scrollTop = fio.scrollHeight;
    }
  }, [itens, enviando, transcrevendo, erro, aberto, maximizado, vendoHistorico, conversa.rascunho]);

  const ocupado = enviando || transcrevendo;
  const temTexto = Boolean(texto.trim()) || anexos.anexos.length > 0;

  async function enviar(falaInformada?: string) {
    if (!(await consentimento.garantir())) return;
    const comAnexo = anexos.anexos.length > 0;
    // Foto sem texto vira a pergunta mais comum: o que tem nela.
    const fala = (falaInformada ?? texto).trim() || (comAnexo ? 'O que tem neste anexo?' : '');
    if (!fala || enviando || anexos.subindo) return;
    if (transcrevendo && falaInformada === undefined) return;
    if (falaInformada === undefined) setTexto('');
    await conversa.enviar(fala, anexos.retirar());
  }

  async function transcreverAudio(audio: AudioGravado) {
    if (enviando || transcrevendo) return;
    if (!(await consentimento.garantir())) return;
    setErro(null);
    setTranscrevendo(true);
    try {
      const resposta = await assistenteService.transcrever(audio);
      const fala = resposta.texto.trim();
      if (!fala) {
        setErro('Nao foi possivel entender o audio.');
        return;
      }
      await enviar(fala);
    } catch (error) {
      setErro(getErrorMessage(error, 'Nao foi possivel transcrever o audio.'));
    } finally {
      setTranscrevendo(false);
    }
  }

  return (
    <div
      className={styles.raiz}
      style={
        {
          '--mia-rodape': `${alturaRodape}px`,
          '--mia-topo': `${topo}px`,
          '--mia-largura': `${doca.largura}px`,
        } as CSSProperties
      }
    >
      {aberto ? (
        <section
          className={[
            styles.painel,
            doca.ancorada ? styles.painelDoca : maximizado ? styles.painelMaximizado : '',
            doca.arrastando ? styles.painelArrastando : '',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-label="Ajuda da Mia"
          data-painel-mia
        >
          {doca.ancorada ? (
            <div
              className={styles.alcaLargura}
              role="separator"
              aria-orientation="vertical"
              aria-label="Ajustar largura da Mia"
              aria-valuemin={doca.minima}
              aria-valuemax={doca.maxima}
              aria-valuenow={doca.largura}
              tabIndex={0}
              {...doca.alca}
              onPointerDown={(event) => {
                setMaximizado(false);
                doca.alca.onPointerDown(event);
              }}
              onKeyDown={(event) => {
                setMaximizado(false);
                doca.alca.onKeyDown(event);
              }}
            />
          ) : null}
          <header className={styles.cabecalho}>
            <h2 className={styles.titulo}>
              <AvatarMia />
              Mia
            </h2>
            <div className={styles.acoesCabecalho}>
              <Tooltip content="Nova conversa" placement="bottom">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Nova conversa"
                  disabled={ocupado}
                  onClick={() => {
                    setVendoHistorico(false);
                    void conversa.novaConversa();
                  }}
                >
                  <SquarePen size={18} />
                </Button>
              </Tooltip>
              <Tooltip content={vendoHistorico ? 'Voltar para a conversa' : 'Conversas anteriores'} placement="bottom">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Conversas anteriores"
                  aria-pressed={vendoHistorico}
                  onClick={() => setVendoHistorico((atual) => !atual)}
                >
                  <History size={18} />
                </Button>
              </Tooltip>
              {itens.length > 0 && !vendoHistorico ? (
                <Tooltip content="Baixar a conversa" placement="bottom">
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="Baixar conversa"
                    onClick={() => baixar('conversa-mia.md', exportarConversa(conversa.titulo, itens))}
                  >
                    <Download size={18} />
                  </Button>
                </Tooltip>
              ) : null}
              <Tooltip content={maximizado ? 'Restaurar tamanho' : 'Expandir'} placement="bottom">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={maximizado ? 'Restaurar ajuda' : 'Maximizar ajuda'}
                  aria-pressed={maximizado}
                  onClick={() => setMaximizado((atual) => !atual)}
                >
                  {maximizado ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                </Button>
              </Tooltip>
              <Tooltip content="Minimizar" placement="bottom">
                <Button variant="ghost" size="sm" aria-label="Minimizar ajuda" onClick={minimizar}>
                  <Minus size={18} />
                </Button>
              </Tooltip>
              <Tooltip content="Fechar" placement="bottom">
                <Button variant="ghost" size="sm" aria-label="Fechar ajuda" onClick={fechar}>
                  <X size={18} />
                </Button>
              </Tooltip>
            </div>
          </header>
          {vendoHistorico ? (
            <HistoricoMia
              idAtual={conversa.idConversa}
              onVoltar={() => setVendoHistorico(false)}
              onAbrir={(escolhida) => {
                setVendoHistorico(false);
                void conversa.abrirConversa(escolhida.id);
              }}
              onArquivouAtual={() => {
                setVendoHistorico(false);
                void conversa.novaConversa();
              }}
            />
          ) : (
            <>
              <div className={styles.fioFundo}>
                <div ref={fioRef} className={styles.fio}>
                  {historicoPronto && itens.length === 0 ? (
                    <AberturaMia
                      key={conversa.idConversa ?? 'nova'}
                      acoes={user?.acoes ?? []}
                      idEscola={user?.idEscolaAtiva ?? null}
                      desabilitado={ocupado}
                      onPerguntar={(pergunta) => void enviar(pergunta)}
                    />
                  ) : null}
                  {itens.map((item, indice) => (
                    <ItemFioMia
                      key={item.id}
                      item={item}
                      ultimaDaMia={item === ultimaDaMia}
                      ocupado={ocupado}
                      onPerguntar={(fala) => void enviar(fala)}
                      onAvaliar={(nota, motivo) =>
                        item.idMensagem ? conversa.avaliar(item.id, item.idMensagem, nota, motivo) : Promise.resolve()
                      }
                      onRegenerar={
                        indice === itens.length - 1 && item.papel === 'assistente' && item.idMensagem && !ocupado && !pendente
                          ? () => void conversa.regenerar(item.id, item.idMensagem!)
                          : undefined
                      }
                      onConfirmar={(campos) =>
                        item.proposta ? conversa.confirmar(item.id, item.proposta, campos) : Promise.resolve()
                      }
                      onCancelar={() =>
                        item.proposta ? conversa.cancelar(item.id, item.proposta) : Promise.resolve()
                      }
                      onFecharDiaAberto={() =>
                        item.proposta && item.diaAberto
                          ? conversa.fecharDiaAberto(item.id, item.proposta, item.diaAberto)
                          : Promise.resolve()
                      }
                    />
                  ))}
                  {transcrevendo && !enviando ? (
                    <p className={styles.statusFio} role="status" aria-label="Transcrevendo">
                      Transcrevendo…
                    </p>
                  ) : null}
                  {enviando && conversa.rascunho.trim() ? (
                    <div className={`${styles.linha} ${styles.linhaAssistente}`}>
                      <AvatarMia />
                      <div className={styles.balaoAssistente} role="status" aria-label="Mia está escrevendo">
                        <TextoAssistente texto={conversa.rascunho} />
                      </div>
                    </div>
                  ) : enviando ? (
                    <div className={`${styles.linha} ${styles.linhaAssistente}`}>
                      <AvatarMia />
                      <div className={styles.balaoDigitando} role="status" aria-label="Mia está digitando">
                        <span className={styles.ponto} />
                        <span className={styles.ponto} />
                        <span className={styles.ponto} />
                        {conversa.etapa && conversa.etapa !== 'Pensando' ? (
                          <span className={styles.etapa}>{conversa.etapa}…</span>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                  {erro ? <p className={styles.aviso} role="alert">{erro}</p> : null}
                </div>
              </div>
              <ListaComandosMia lista={comandos.lista} atual={comandos.atual} onEscolher={comandos.escolher} />
              <AnexosMia anexos={anexos.anexos} onRemover={anexos.remover} />
              <form
                className={styles.composer}
                onSubmit={(event) => {
                  event.preventDefault();
                  void enviar();
                }}
              >
                {gravando ? null : (
                  <>
                    <input
                      ref={arquivoRef}
                      type="file"
                      accept="image/*,application/pdf"
                      multiple
                      hidden
                      aria-label="Escolher foto ou PDF"
                      onChange={(event) => {
                        const escolhidos = Array.from(event.target.files ?? []);
                        event.target.value = '';
                        void anexos.adicionar(escolhidos);
                      }}
                    />
                    <button
                      type="button"
                      className={styles.anexar}
                      aria-label="Anexar foto ou PDF"
                      disabled={ocupado || anexos.subindo || anexos.anexos.length >= MAX_ANEXOS_MIA}
                      onClick={() => arquivoRef.current?.click()}
                    >
                      {anexos.subindo ? <Loader2 size={18} className={styles.girando} /> : <Paperclip size={18} />}
                    </button>
                    <input
                      ref={campoRef}
                      className={styles.campo}
                      value={texto}
                      maxLength={2000}
                      aria-label="Mensagem para a Mia"
                      placeholder="Pergunte a Mia"
                      disabled={ocupado}
                      onChange={(event) => setTexto(event.target.value)}
                      onKeyDown={(event) => comandos.onKeyDown(event, () => setTexto(''))}
                      aria-expanded={comandos.lista.length > 0}
                      onPaste={(event) => {
                        const colados = Array.from(event.clipboardData?.files ?? []);
                        if (colados.length === 0) return;
                        event.preventDefault();
                        void anexos.adicionar(colados);
                      }}
                    />
                  </>
                )}
                {temTexto && !gravando ? (
                  <Button type="submit" size="sm" className={styles.enviar} disabled={ocupado} aria-label="Enviar">
                    <Send size={16} />
                  </Button>
                ) : (
                  <AudioRecorderButton
                    disabled={ocupado}
                    maxSegundos={60}
                    maxBytes={2 * 1024 * 1024}
                    onGravandoChange={setGravando}
                    onErro={setErro}
                    onGravado={(audio) => void transcreverAudio(audio)}
                  />
                )}
              </form>
            </>
          )}
        </section>
      ) : null}
      {presente && !aberto ? <BotaoMia onAbrir={abrir} /> : null}
      {recolhida && !aberto ? (
        <button type="button" className={styles.voltarMia} onClick={abrir}>
          <AvatarMia />
          Voltar para a Mia
        </button>
      ) : null}
    </div>
  );
}

/** Altura da barra fixa de salvar (rodape) e topo da area de conteudo (abaixo do cabecalho e avisos). */
function useMedidasShell(ativo: boolean) {
  const [medidas, setMedidas] = useState({ rodape: 0, topo: 56 });
  useEffect(() => {
    if (!ativo) return;
    let quadro = 0;
    const medir = () => {
      const rodape = alturaRodapeAcao();
      const main = document.querySelector('main');
      const topo = main ? Math.max(0, Math.round(main.getBoundingClientRect().top)) : 56;
      setMedidas((atual) => (atual.rodape === rodape && atual.topo === topo ? atual : { rodape, topo }));
    };
    const agendar = () => {
      cancelAnimationFrame(quadro);
      quadro = requestAnimationFrame(medir);
    };
    agendar();
    const observador = new MutationObserver(agendar);
    observador.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', agendar);
    return () => {
      cancelAnimationFrame(quadro);
      observador.disconnect();
      window.removeEventListener('resize', agendar);
    };
  }, [ativo]);
  return medidas;
}
